import { Skeleton, useToast } from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isWipModified } from '@finley-technologies/credit-agreement-utils';

import { CreditAgreementFullArtifactWithIntrospectionEntityFetch } from './CreditAgreementFullArtifactWithIntrospectionEntityFetch';
import { CreditAgreementWipHeader } from './index';
import { createOrganizationMap } from './utils/createMaps';
import { useGetCommonWipData } from './utils/useGetCommonWipData';
import { useGetCurrentCaAndWip } from './utils/useGetCurrentCaAndWip';
import { createWipSchemaValidation } from './utils/wipSchemaValidation';
import { useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery } from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { CreditAgreementContextProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { useSaveClientWipCreditAgreement } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/useSaveClientWipCreditAgreement';
import {
  CreditAgreement,
  CreditAgreementFullArtifactWipConfigType,
} from '~/packages/creditAgreements/ui/types';
import { useVisibleOrganizationsQuery } from '~/packages/implementations/requests/implementations.gql.generated';

/** Body override passed through to the introspection artifact renderer. */
type ArtifactComponent = React.ComponentType<{
  organizationId: string;
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
}>;

type ClientCreditAgreementWipProps = {
  organizationId: string;
  implementationId: string;
  creditFacilityId: string;
  /**
   * Where to navigate after a successful commit or WIP removal (typically the
   * credit-agreements list, or — in the BUS portal — the deal overview).
   */
  onDonePath: string;
  /**
   * Optional artifact body to render instead of the full credit agreement — e.g.
   * `BusCreditAgreementFullArtifact` for a `backupServicing` deal. Prop-compatible
   * bodies drop in and reuse the whole introspection fetch / wipConfig machinery.
   */
  ArtifactComponent?: ArtifactComponent;
  /** Trims the WIP header for the simplified (BUS) editor. */
  simplifiedHeader?: boolean;
  /**
   * Renders the editor read-only: no Save/Commit/discard in the header and no
   * add/edit/delete controls in any section. The full config stays visible.
   */
  readOnly?: boolean;
};

/**
 * Client-safe credit-agreement WIP editor, decoupled from routing.
 *
 * Fetches the current CA + WIP via introspection (a client cannot read every org
 * / user / bank account the admin can), wires Formik + the shared WIP header, and
 * saves through the org-scoped `updateClientCreditAgreementFullArtifactWip`
 * mutation. Both the core `/implementations/.../credit-agreements/wip` route and
 * the BUS lender portal's deal-settings route render this — the latter injects
 * the simplified BUS artifact body via `ArtifactComponent`.
 */
const ClientCreditAgreementWipInner = ({
  organizationId,
  implementationId,
  creditFacilityId,
  onDonePath,
  ArtifactComponent,
  simplifiedHeader = false,
  readOnly = false,
  creditAgreementFullArtifactWip,
  currentCreditAgreementFullArtifact,
}: ClientCreditAgreementWipProps & {
  creditAgreementFullArtifactWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  currentCreditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [arePartiesLocked, setArePartiesLocked] = useState(false);
  const [isDiffOpen, setIsDiffOpen] = useState<boolean>(false);
  const [isJsonUploadOpen, setIsJsonUploadOpen] = useState(false);

  const {
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
  } = useSaveClientWipCreditAgreement({ creditFacilityId });

  const { loanProductsMap, tAccounts, facilityType } = useGetCommonWipData({
    creditFacilityId,
  });

  const visibleOrganizationsQuery = useVisibleOrganizationsQuery({
    variables: { organizationId },
  });

  const {
    data: bankAccountsWithInstitutionsData,
  } = useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery({
    variables: { implementationId },
  });

  const isWipDirtyCallback = useCallback(
    (newWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact) => {
      return isWipModified(creditAgreementFullArtifactWip, newWip);
    },
    [creditAgreementFullArtifactWip],
  );

  // Early return if required data is not available
  if (
    !tAccounts ||
    !facilityType ||
    !loanProductsMap ||
    !visibleOrganizationsQuery.data ||
    !bankAccountsWithInstitutionsData?.implementation
      .bankAccountsWithInstitutionAndOwner
  ) {
    return <Skeleton height="150px" />;
  }

  // Create organization map using introspection (from existing WIP data)
  const parties = creditAgreementFullArtifactWip.facilityMechanics.parties;
  const allParties = [
    ...(parties.borrowers || []),
    ...(parties.capitalProviders || []),
    ...(parties.servicers || []),
    ...(parties.adminAgents || []),
    ...(parties.backupServicers || []),
    ...(parties.collectionAccountBanks || []),
    ...(parties.payingAgents || []),
    ...(parties.verificationAgents || []),
  ].filter(Boolean); // Remove any undefined/null entries
  const organizations = allParties
    .filter((party) => party && party.entity)
    .map((party) => party.entity);

  // also include all the orgs from visibleOrganizations in the CA org map
  // createOrganizationMap will handle any dups
  visibleOrganizationsQuery.data.organization.visibleOrganizations.forEach(
    (org) => {
      organizations.push(org);
    },
  );

  const organizationMap = createOrganizationMap(organizations);

  return (
    <Formik
      enableReinitialize
      initialValues={creditAgreementFullArtifactWip}
      validationSchema={createWipSchemaValidation()}
      onSubmit={async (values) => {
        try {
          await saveCreditAgreementFullArtifactWip({
            variables: {
              creditAgreementFullArtifactWip: {
                ...values,
                creditAgreement: JSON.stringify(values), // Serialize the full artifact
              },
            },
          });
          toast({
            title: 'Credit agreement WIP saved successfully',
            status: 'success',
          });
        } catch (err: any) {
          toast({
            ...errorToastConfig,
            title: getUnexpectedErrorMessage(err),
          });
        }
      }}
    >
      {(
        formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>,
      ) => {
        const isWipDirty = isWipDirtyCallback(formProps.values);
        const artifactDocumentId =
          creditAgreementFullArtifactWip.borrowingBaseMechanics
            .calcAgentMechanics._artifactDocumentId_ || null;

        return (
          <Form onSubmit={formProps.handleSubmit}>
            <CreditAgreementContextProvider
              initOrgSlugs={organizations.map(({ slug }) => slug)}
              parties={formProps.values.facilityMechanics.parties}
            >
              <CreditAgreementWipHeader
                isWipDirty={isWipDirty}
                isDiffOpen={isDiffOpen}
                setIsDiffOpen={setIsDiffOpen}
                creditFacilityId={creditFacilityId}
                formProps={formProps}
                artifactDocumentId={artifactDocumentId}
                currentCreditAgreementFullArtifact={
                  currentCreditAgreementFullArtifact
                }
                isJsonUploadOpen={isJsonUploadOpen}
                setIsJsonUploadOpen={setIsJsonUploadOpen}
                operationMode="client"
                simplified={simplifiedHeader}
                readOnly={readOnly}
                saveCreditAgreementFullArtifactWip={
                  saveCreditAgreementFullArtifactWip
                }
                isSaveCreditAgreementFullArtifactWipLoading={
                  isSaveCreditAgreementFullArtifactWipLoading
                }
                onCommitDone={() => navigate(onDonePath)}
                onRemoveWipSuccess={() => navigate(onDonePath)}
              />
              <CreditAgreementFullArtifactWithIntrospectionEntityFetch
                organizationId={organizationId}
                creditAgreementFullArtifact={formProps.values}
                organizationMap={organizationMap}
                tAccounts={tAccounts}
                facilityType={facilityType}
                formProps={formProps}
                creditFacilityId={creditFacilityId}
                implementationId={implementationId}
                arePartiesLocked={arePartiesLocked}
                setArePartiesLocked={setArePartiesLocked}
                loanProductsMap={loanProductsMap}
                // this is admin because we want the same functionality as admin version
                // operation mode `client` is used to conditional render "implementation portal" items
                operationMode="admin"
                ArtifactComponent={ArtifactComponent}
                readOnly={readOnly}
                saveCreditAgreementFullArtifactWip={
                  saveCreditAgreementFullArtifactWip
                }
                isSaveCreditAgreementFullArtifactWipLoading={
                  isSaveCreditAgreementFullArtifactWipLoading
                }
                saveAndUpdateConfigCreditAgreementWip={async (inputs) => {
                  // For client introspection, we don't need to update config
                  // Just save the current WIP state with the updated property
                  const currentValues = inputs.wipConfig.formProps.values;
                  await saveCreditAgreementFullArtifactWip({
                    variables: {
                      creditAgreementFullArtifactWip: {
                        ...currentValues,
                        creditAgreement: JSON.stringify(currentValues),
                      },
                    },
                  });
                }}
                bankAccountsWithInstitutionAndOwner={
                  bankAccountsWithInstitutionsData?.implementation
                    .bankAccountsWithInstitutionAndOwner
                }
              />
            </CreditAgreementContextProvider>
          </Form>
        );
      }}
    </Formik>
  );
};

export const ClientCreditAgreementWip = (
  props: ClientCreditAgreementWipProps,
): React.ReactElement => {
  const {
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
  } = useGetCurrentCaAndWip({
    creditFacilityId: props.creditFacilityId,
    creditAgreementsPath: props.onDonePath,
  });

  if (!creditAgreementFullArtifactWip || !currentCreditAgreementFullArtifact) {
    return <Skeleton height="150px" />;
  }

  return (
    <ClientCreditAgreementWipInner
      {...props}
      creditAgreementFullArtifactWip={creditAgreementFullArtifactWip}
      currentCreditAgreementFullArtifact={currentCreditAgreementFullArtifact}
    />
  );
};
